import type { CoordeRun, CoordeSet } from '@cortex/domain-ac-mens-hub-api';
import { summarizeRun } from '@cortex/domain-ac-mens-hub-api';
import { useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCoordeRun, useSetCoordeApproval } from '../hooks/useCoordeRun.js';
import { buildCoordeImageUrl } from '../lib/coorde-image.js';

/**
 * 生成結果の確認と承認。
 *
 * **承認されていない画像は素材として使えない**（BR-5）。この画面がその唯一のゲートなので、
 * 「まだ見ていないものが何本あるか」を常に見えるようにし、判定を飛ばせないようにしている。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Styling, Marketing
 * @graph-business Lets a person look at every generated outfit and approve or reject it, which is the gate that decides what may be used as material.
 * @graph-connects useCoordeRun [calls] ランの進捗・結果取得
 * @graph-connects useSetCoordeApproval [calls] 判定の記録
 */

/** 表示の絞り込み。 @graph-connects none */
export type CoordeSetFilter = 'all' | 'unjudged';

/**
 * 判定待ちのセットだけに絞る。
 *
 * 画像が出来ていないもの（未着手・失敗）は判定できないので「未判定」には数えない。
 * ここを間違えると、生成に失敗した本数のぶんだけ永久に終わらない画面になる。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling, Marketing
 * @graph-business Narrows the grid to the outfits still waiting for a human decision, without counting ones that never produced an image.
 * @graph-connects none
 */
export function filterCoordeSets(sets: readonly CoordeSet[], filter: CoordeSetFilter): CoordeSet[] {
  const ordered = [...sets].sort((a, b) => a.index - b.index);
  if (filter === 'all') return ordered;
  return ordered.filter((set) => set.status === 'generated' && set.approval === 'pending');
}

/**
 * カードに出すアイテムの説明文を組み立てる。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling, Marketing
 * @graph-business Summarises which garments an outfit used, so a reviewer can tell the image apart from the real products.
 * @graph-connects none
 */
export function describeCoordeItems(set: CoordeSet): string {
  return set.items.map((item) => `${item.brandName} ${item.itemName}`).join(' / ');
}

/**
 * 判定の進み具合を1行で表す。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling, Marketing
 * @graph-business States how much of a run still needs a human decision, which is the only thing blocking the material from being usable.
 * @graph-connects summarizeRun [calls] ランの件数集計
 */
export function countUnjudged(run: CoordeRun): number {
  return summarizeRun(run).unjudgedCount;
}

/** 統計1マス。 @graph-connects none */
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-[var(--color-surface)] px-4 py-3">
      <dt className="text-[12px] text-[var(--color-text-muted)]">{label}</dt>
      <dd className="mt-1 text-[22px] font-semibold tabular-nums text-[var(--color-text-primary)]">
        {value}
      </dd>
    </div>
  );
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling, Marketing
 * @graph-business Shows every outfit produced by one generation run with its image, and records the approve/reject decision that gates whether the material may be used.
 * @graph-connects useCoordeRun [calls] ランの取得
 * @graph-connects useSetCoordeApproval [calls] 判定の記録
 */
export function CoordeRunPage() {
  const { t } = useTranslation();
  const { runId } = useParams({ from: '/coorde/$runId' });
  const { data: run, isLoading, error } = useCoordeRun(runId);
  const approval = useSetCoordeApproval();

  const [filter, setFilter] = useState<CoordeSetFilter>('all');
  const [rejectingSetId, setRejectingSetId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const sets = useMemo(() => filterCoordeSets(run?.sets ?? [], filter), [run, filter]);
  const summary = useMemo(() => (run ? summarizeRun(run) : null), [run]);

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-[var(--color-text-muted)]">
        {t('app.loading')}
      </div>
    );
  }

  if (error || !run || !summary) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-[var(--color-error)]">
        {t('common.fetchError')}
      </div>
    );
  }

  const judge = (set: CoordeSet, verdict: 'approved' | 'rejected', reason: string | null) => {
    approval.mutate({ runId, setId: set.setId, approval: verdict, rejectReason: reason });
    setRejectingSetId(null);
    setRejectReason('');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[20px] font-semibold tracking-tight text-[var(--color-text-primary)]">
          {t('coordeRun.title')}
        </h1>
        <p className="mt-1 text-[13px] text-[var(--color-text-muted)]">
          {t('coordeRun.meta', {
            season: t(`itemCatalog.season.${run.season}`),
            requestedBy: run.requestedBy,
            createdAt: new Date(run.createdAt).toLocaleString('ja-JP'),
          })}
        </p>
      </div>

      <dl className="grid grid-cols-4 gap-px overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-border)]">
        <Stat label={t('coordeRun.stat.total')} value={String(summary.setCount)} />
        <Stat label={t('coordeRun.stat.generated')} value={String(summary.generatedCount)} />
        <Stat label={t('coordeRun.stat.approved')} value={String(summary.approvedCount)} />
        <Stat label={t('coordeRun.stat.unjudged')} value={String(summary.unjudgedCount)} />
      </dl>

      {run.status === 'queued' || run.status === 'running' ? (
        <p className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-[13px] text-[var(--color-text-muted)]">
          {t('coordeRun.inProgress', {
            done: summary.generatedCount + summary.failedCount,
            total: summary.setCount,
          })}
        </p>
      ) : null}

      {approval.isError ? (
        <p
          role="alert"
          className="rounded-md border border-[var(--color-error)] px-4 py-3 text-[13px] text-[var(--color-error)]"
        >
          {t('coordeRun.approvalError')}:{' '}
          {approval.error instanceof Error ? approval.error.message : t('common.unknownError')}
        </p>
      ) : null}

      <div className="flex gap-2">
        {(['all', 'unjudged'] as const).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => {
              setFilter(value);
            }}
            className={`rounded-md border px-3 py-1.5 text-[13px] ${
              filter === value
                ? 'border-[var(--color-accent-primary)] text-[var(--color-text-primary)]'
                : 'border-[var(--color-border)] text-[var(--color-text-muted)]'
            }`}
          >
            {t(`coordeRun.filter.${value}`)}
          </button>
        ))}
      </div>

      {sets.length === 0 ? (
        <p className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-6 text-center text-[13px] text-[var(--color-text-muted)]">
          {t('coordeRun.empty')}
        </p>
      ) : (
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {sets.map((set) => (
            <li
              key={set.setId}
              className="overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]"
            >
              {set.status === 'generated' ? (
                <img
                  src={buildCoordeImageUrl(runId, set.setId)}
                  alt={t('coordeRun.imageAlt', { index: set.index })}
                  className="aspect-4/5 w-full bg-[var(--color-border)] object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="flex aspect-4/5 w-full items-center justify-center bg-[var(--color-border)] px-4 text-center text-[13px] text-[var(--color-text-muted)]">
                  {set.status === 'failed'
                    ? (set.errorMessage ?? t('coordeRun.failed'))
                    : t('coordeRun.waiting')}
                </div>
              )}

              <div className="space-y-3 px-4 py-3">
                <p className="text-[13px] text-[var(--color-text-primary)]">
                  {describeCoordeItems(set)}
                </p>

                {set.status === 'generated' ? (
                  <div className="space-y-2">
                    <p className="text-[12px] text-[var(--color-text-muted)]">
                      {t(`coordeRun.approval.${set.approval}`)}
                      {set.approval === 'rejected' && set.rejectReason !== null
                        ? ` — ${set.rejectReason}`
                        : ''}
                    </p>

                    {rejectingSetId === set.setId ? (
                      <div className="space-y-2">
                        <textarea
                          value={rejectReason}
                          onChange={(e) => {
                            setRejectReason(e.target.value);
                          }}
                          rows={2}
                          maxLength={500}
                          placeholder={t('coordeRun.rejectReasonPlaceholder')}
                          className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-[13px] text-[var(--color-text-primary)]"
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              judge(set, 'rejected', rejectReason === '' ? null : rejectReason);
                            }}
                            className="rounded-md border border-[var(--color-error)] px-3 py-1.5 text-[13px] text-[var(--color-error)]"
                          >
                            {t('coordeRun.confirmReject')}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setRejectingSetId(null);
                              setRejectReason('');
                            }}
                            className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-[13px] text-[var(--color-text-muted)]"
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            judge(set, 'approved', null);
                          }}
                          disabled={approval.isPending}
                          className="rounded-md border border-[var(--color-accent-primary)] px-3 py-1.5 text-[13px] text-[var(--color-text-primary)] disabled:opacity-50"
                        >
                          {t('coordeRun.approve')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setRejectingSetId(set.setId);
                            setRejectReason('');
                          }}
                          disabled={approval.isPending}
                          className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-[13px] text-[var(--color-text-muted)] disabled:opacity-50"
                        >
                          {t('coordeRun.reject')}
                        </button>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="text-[12px] text-[var(--color-text-muted)]">{t('coordeRun.note')}</p>
    </div>
  );
}
